import { useCallback, useState } from 'react'

import { bugReportClient } from 'clients/bug-report'
import { useAuthContext } from 'features/auth/auth-context'
import { useBugReportAttachments } from 'features/bug-report/hooks/use-bug-report/use-bug-report-attachments'
import { useBugReportOverlay } from 'features/bug-report/hooks/use-bug-report/use-bug-report-overlay'
import {
  BUG_REPORT_EVENT,
  BugReportTrackingAction,
} from 'features/bug-report/mixpanel'
import type {
  Attachment,
  SelectedRegion,
  SubmitResult,
} from 'features/bug-report/types'
import { buildBugReport } from 'features/bug-report/utils/build-report'
import {
  captureFullPage,
  captureRegion,
} from 'features/bug-report/utils/screenshot'
import type { UploadProcessResult } from 'features/bug-report/utils/uploads'
import mixpanel from 'trackers/mixpanel'

type UseBugReportArgs = {
  projectId?: string
}

export type BugReportState = {
  isOpen: boolean
  shouldShowOverlay: boolean
  visibleAttachments: Attachment[]
}

export type BugReportActions = {
  openModal: () => Promise<void>
  closeModal: () => void
  startRegionSelect: () => void
  completeRegionSelect: (region: SelectedRegion) => Promise<boolean>
  cancelRegionSelect: () => void
  addUploads: (files: File[]) => Promise<UploadProcessResult>
  removeAttachment: (id: string) => void
  submit: (message: string) => Promise<SubmitResult>
}

export type UseBugReportReturn = {
  state: BugReportState
  actions: BugReportActions
}

export function useBugReport({
  projectId,
}: UseBugReportArgs): UseBugReportReturn {
  const { userDetails } = useAuthContext()
  const attachments = useBugReportAttachments()
  const overlay = useBugReportOverlay()
  const [isOpen, setIsOpen] = useState(false)

  const openModal = useCallback(async (): Promise<void> => {
    mixpanel.track(BUG_REPORT_EVENT, {
      action: BugReportTrackingAction.OPEN,
      proj_uuid: projectId,
    })
    setIsOpen(true)
    const dataUrl = await captureFullPage()
    if (dataUrl) attachments.setAutoShot(dataUrl)
  }, [projectId, attachments])

  const closeModal = useCallback((): void => {
    setIsOpen(false)
    attachments.reset()
  }, [attachments])

  const startRegionSelect = useCallback((): void => {
    setIsOpen(false)
    overlay.startSelecting()
  }, [overlay])

  const completeRegionSelect = useCallback(
    async (region: SelectedRegion): Promise<boolean> => {
      overlay.finishSelecting()
      const dataUrl = await captureRegion(region)
      if (dataUrl) {
        attachments.addRegionShot(dataUrl)
        mixpanel.track(BUG_REPORT_EVENT, {
          action: BugReportTrackingAction.REGION_CAPTURE,
          proj_uuid: projectId,
        })
      }
      setIsOpen(true)
      return dataUrl !== null
    },
    [overlay, attachments, projectId],
  )

  const cancelRegionSelect = useCallback((): void => {
    overlay.finishSelecting()
    setIsOpen(true)
  }, [overlay])

  const submit = useCallback(
    async (message: string): Promise<SubmitResult> => {
      const report = buildBugReport({
        message,
        attachments: attachments.allAttachments,
        reporter: userDetails
          ? {
              firstName: userDetails.first_name,
              lastName: userDetails.last_name,
              email: userDetails.email,
            }
          : null,
        projectId,
        url: window.location.href,
        pathname: window.location.pathname,
      })
      try {
        await bugReportClient.submit(report)
        mixpanel.track(BUG_REPORT_EVENT, {
          action: BugReportTrackingAction.SUBMIT,
          proj_uuid: projectId,
          value: { screenshot_count: report.screenshots.length },
        })
        return { ok: true }
      } catch {
        return { ok: false }
      }
    },
    [userDetails, attachments.allAttachments, projectId],
  )

  return {
    state: {
      isOpen,
      shouldShowOverlay: overlay.shouldShowOverlay,
      visibleAttachments: attachments.visibleAttachments,
    },
    actions: {
      openModal,
      closeModal,
      startRegionSelect,
      completeRegionSelect,
      cancelRegionSelect,
      addUploads: attachments.addUploads,
      removeAttachment: attachments.removeAttachment,
      submit,
    },
  }
}
